import JSZip from 'jszip';
import * as THREE from 'three';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';

export function useDataValidation() {
  const sanitizeModuleData = (module) => {
    const data = module.data || {};
    const keys = Object.keys(data);

    for (const key of keys) {
      const val = data[key];
      if (typeof val === 'number') {
        Object.assign(data, { [key]: parseFloat(val.toFixed(2)) });
      }
    }

    module.data = data;
    return module;
  };

  const validateProjectorMesh = async (file) => {
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['obj', 'zip'].includes(ext)) {
      throw new Error('Unsupported file type. Only .obj and .zip files are allowed.');
    }

    const buffer = await file.arrayBuffer();

    // File size check (max 10MB)
    if (buffer.byteLength > 10 * 1024 * 1024) {
      throw new Error('Mesh file too large (max 10MB).');
    }

    // Decode — same logic as newProjector._decodeBuffer
    const header = new Uint8Array(buffer, 0, 2);
    let text;
    if (header[0] === 0x50 && header[1] === 0x4b) {
      const zip = await JSZip.loadAsync(buffer);
      const zipFile = Object.values(zip.files).find((f) => !f.dir);
      if (!zipFile) throw new Error('ZIP archive contains no files');
      text = await zipFile.async('string');
    } else {
      text = new TextDecoder().decode(buffer);
    }

    // Parse — same logic as newProjector._loadMesh
    let loaded;
    try {
      loaded = text.trimStart().startsWith('{')
        ? new THREE.ObjectLoader().parse(JSON.parse(text))
        : new OBJLoader().parse(text);
    } catch (e) {
      throw new Error('Failed to parse file. Must be a valid OBJ or Three.js JSON model.');
    }

    // Find meshes
    const meshes = [];
    loaded.traverse((child) => {
      if (child.isMesh) meshes.push(child);
    });
    if (meshes.length === 0) {
      throw new Error('No 3D meshes found in file.');
    }
    if (meshes.length > 2) {
      throw new Error('Too many meshes. Maximum 2 allowed.');
    }

    // Identify body/lens
    const bodyMesh = meshes.find((m) => m.name.toLowerCase().includes('body')) ?? meshes[0];
    const lensMesh = meshes.find((m) => m.name.toLowerCase().includes('lens'));

    // Vertex count check (max 100k vertices)
    const geo = bodyMesh.geometry;
    const positions = geo.attributes.position;
    if (positions.count > 10000) {
      throw new Error(`Too many vertices (${positions.count}).`);
    }

    return { valid: true };
  };
  const sanitizeNumber = (value, { min = 0, max = 1, fallback = 0 } = {}) => {
    const num = Number(value);
    if (!isFinite(num)) return fallback;
    return Math.max(min, Math.min(max, num));
  };

  return {
    sanitizeNumber,
    sanitizeModuleData,
    validateProjectorMesh,
  };
}
